import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/* ─────────────────────  THE NESTED-DIALOG STACK  ──────────────────────
 *
 * Every open dialog registers its own `document` keydown listener, and
 * `preventDefault()` does NOT stop sibling listeners on the same node — so one
 * Escape used to run EVERY open dialog's dismiss. That is proven, not theorised:
 * `use-dialog-focus.test.tsx` fails on the unpatched hook with both the outer and
 * the inner `onDismiss` called by a single key. With a unit form mounted on top
 * of a building modal, that one Escape threw away unsaved building edits.
 *
 * So the layers share one module-level stack and only the TOPMOST one reacts to
 * a key. Escape belongs to the layer the user sees on top, and so does the Tab
 * trap — while an inner layer is open the outer layer must stop pulling focus
 * back into itself.
 *
 * Ordering is LIFO by MOUNT ORDER, which is correct for every real case: a
 * nested layer is opened by a click inside the layer below it, so it mounts in a
 * later commit and lands on top. The one shape that would order wrongly is two
 * layers mounting in the SAME commit with one nested inside the other — React
 * runs child effects before parent effects, so the inner one would push first.
 * No screen does that today, and none should: open the inner layer from a state
 * flip (a second commit), never on the outer layer's first render.
 *
 * Not every dialog in the app uses this module. `profile.tsx:62` and the
 * row-action menus in `dashboard-buildings.tsx:248` / `dashboard-portfolios.tsx:236`
 * keep their own unconditional Escape listeners; those are menus rather than
 * layers, and they are left alone deliberately.
 */

interface DialogToken {
  readonly id: number;
  /* Does THIS layer hold the page still while it is open?
   *
   * A modal does: the page behind it is not usable and scrolling it is a bug. A
   * corner panel that sits over a page the reader is still meant to read does
   * NOT — and the IQ chat is exactly that. It joined this stack purely for
   * Escape ORDERING (so one key does not close it and a dashboard modal
   * together) and got the scroll lock as a side effect, freezing the page it
   * was opened to ask questions about. Its own comment says it "deliberately
   * does not lock body scroll"; this is what makes that true. */
  readonly locksScroll: boolean;
}

const dialogStack: DialogToken[] = [];
let nextDialogId = 0;

/* The body scroll lock belongs to the STACK, not to any one layer, for the same
 * reason `removeDialog` works by identity: teardown order is React's business.
 *
 * Each layer used to save the overflow it found and restore that value on the
 * way out. That is a save/restore stack, and it is only correct if unmounts are
 * the exact reverse of mounts. Closing the building modal while the unit form is
 * still open breaks that: React runs the parent cleanup first, so the outer layer
 * restored '' and the inner layer then restored the 'hidden' IT had captured —
 * leaving the page scroll-locked with no dialog on screen.
 *
 * So the value is captured once, when the stack goes empty -> non-empty, and
 * restored once, when it goes back to empty. Any order in between is fine. */
let lockedOverflow: string | null = null;

/* Counted separately from the stack: the lock belongs to the LOCKING layers,
   and a non-locking panel opening on top of a modal must not release it. */
const lockingCount = (): number =>
  dialogStack.filter(entry => entry.locksScroll).length;

const pushDialog = (locksScroll = true): DialogToken => {
  nextDialogId += 1;

  const token: DialogToken = { id: nextDialogId, locksScroll };

  if (locksScroll && lockingCount() === 0) {
    lockedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  dialogStack.push(token);

  return token;
};

/* Removed BY IDENTITY rather than with `pop()`: when a whole subtree is torn
 * down at once, cleanup order is React's business and is not guaranteed to be
 * the exact reverse of mount order. A blind `pop()` would evict the wrong
 * layer and leave a live dialog believing it is not topmost — permanently deaf
 * to Escape. */
const removeDialog = (token: DialogToken): void => {
  const at = dialogStack.indexOf(token);

  if (at !== -1) {
    dialogStack.splice(at, 1);
  }

  /* Last LOCKING layer out releases the lock, whichever one it happens to be.
     Counted rather than "is the stack empty", so a non-locking panel left open
     over a closed modal does not keep the page frozen. */
  if (lockingCount() === 0 && lockedOverflow !== null) {
    document.body.style.overflow = lockedOverflow;
    lockedOverflow = null;
  }
};

const isTopDialog = (token: DialogToken): boolean =>
  dialogStack.length > 0 && dialogStack[dialogStack.length - 1] === token;

export const useDialogFocus = <T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onDismiss: () => void
) => {
  const dialogRef = useRef<T>(null);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!open) {
      return;
    }

    /* pushDialog owns the body scroll lock — see the note on the stack above. */
    const token = pushDialog();
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

    /* WHERE FOCUS LANDS IS A SAFETY DECISION, NOT A DOM ACCIDENT.
     *
     * This focused `focusable[0]` — the first focusable element in source order
     * — and the delete-portfolio dialog happens to render its destructive
     * button first. So opening that confirmation put the keyboard on "Delete
     * portfolio", and a single Enter or Space destroyed the portfolio: the
     * confirmation step became a one-key accelerator for the thing it exists to
     * prevent.
     *
     * A dialog can now name its own landing point with `data-autofocus`, which
     * ConfirmDialog puts on the DISMISS control. Nothing else changes: a form
     * modal with no marker still lands on its first field, which is right. */
    const preferred = dialog?.querySelector<HTMLElement>('[data-autofocus]');

    (preferred || focusable?.[0] || dialog)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      /* A layer with something on top of it is a spectator: the topmost layer
       * owns both Escape and the Tab trap. */
      if (!isTopDialog(token)) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        dismissRef.current();

        return;
      }

      if (event.key !== 'Tab' || !dialog) {
        return;
      }

      const items = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );

      if (items.length === 0) {
        event.preventDefault();
        dialog.focus();

        return;
      }

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      /* removeDialog releases the body lock once the LAST layer leaves, so this
       * cleanup never touches body.style itself — that is what makes tearing a
       * whole subtree down in any order safe. */
      removeDialog(token);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open]);

  return dialogRef;
};

/* Escape-only participation in the same stack, for a dialog that owns its own
 * focus and scroll handling and therefore cannot take the whole hook. The
 * floating IQ chat is the case: it is mounted app-wide (`App.tsx:66`), so it can
 * sit on top of a dashboard modal, and its own listener used to close both. */
export const useDialogEscape = (
  open: boolean,
  onEscape: () => void,
  options: { locksScroll?: boolean } = {}
): void => {
  const { locksScroll = true } = options;
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const token = pushDialog(locksScroll);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !isTopDialog(token)) {
        return;
      }

      event.preventDefault();
      escapeRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      removeDialog(token);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, locksScroll]);
};
